import { ContactAdd } from '#app/routes/mocra+/_form+/components/contacts/add.tsx'
import { ContactCard } from '#app/routes/mocra+/_form+/components/contacts/card.tsx'
import { type ContactDraft } from '#app/routes/mocra+/_form+/components/contacts/types.ts'
import { Section } from '#app/routes/mocra+/_form+/components/section.tsx'
import { type ContactDisplay } from '#app/routes/mocra+/_form+/contacts/reference.ts'
import { type ChangeControlStageKey } from '#app/routes/mocra+/_form+/regulatory-monitoring.tsx'
import { type SectionResources } from '#app/routes/mocra+/_form+/section-resources.ts'

type ChangeControlContactItem = {
	readonly display: ContactDisplay
	readonly errors: readonly string[]
}

type ChangeControlStageView = {
	readonly key: ChangeControlStageKey
	readonly label: string
	readonly question: string
	readonly defaultName: string
	readonly defaultTitle: string
	readonly directorySource: string
	readonly contacts: ReadonlyArray<ChangeControlContactItem>
	readonly existingContacts: ReadonlyArray<ContactDraft>
	readonly listErrors: readonly string[]
}

type Props = {
	readonly resources: SectionResources
	readonly stages: ReadonlyArray<ChangeControlStageView>
	readonly onAddContact: (
		stage: ChangeControlStageKey,
		draft: ContactDraft,
	) => void
	readonly onEditContact: (stage: ChangeControlStageKey, index: number) => void
	readonly onRemoveContact: (
		stage: ChangeControlStageKey,
		index: number,
	) => void
}

export function ChangeControlSection({
	resources,
	stages,
	onAddContact,
	onEditContact,
	onRemoveContact,
}: Props) {
	return (
		<Section
			title="Change-control approvals"
			description="Clarify who reviews, approves, implements, and closes out regulatory-driven changes."
			mocraSections={resources.mocraSections}
			templates={resources.templates}
			childrenClassName="space-y-6"
		>
			{stages.map((stage) => (
				<div key={stage.key} className="space-y-3">
					<p className="text-sm font-medium">{stage.question}</p>
					{stage.listErrors.length ? (
						<ul className="text-destructive list-disc space-y-1 pl-5 text-xs">
							{stage.listErrors.map((error) => (
								<li key={error}>{error}</li>
							))}
						</ul>
					) : null}
					{stage.contacts.length > 0 ? (
						<div className="space-y-2">
							{stage.contacts.map((contact, index) => (
								<div key={`${stage.key}-${index}`} className="space-y-1">
									<ContactCard
										name={contact.display.name}
										title={contact.display.title}
										email={contact.display.email}
										phone={contact.display.phone}
										onClick={() => onEditContact(stage.key, index)}
										onRemove={() => onRemoveContact(stage.key, index)}
										ariaLabel={`${stage.label} contact ${index + 1}`}
										className={
											contact.errors.length > 0
												? 'border-destructive/40 ring-destructive/40 ring-1'
												: undefined
										}
									/>
									{contact.errors.length > 0 ? (
										<p className="text-destructive text-sm">
											{contact.errors[0]}
										</p>
									) : null}
								</div>
							))}
						</div>
					) : null}
					<ContactAdd
						onSubmit={(draft) => onAddContact(stage.key, draft)}
						directorySource={stage.directorySource}
						existingContacts={stage.existingContacts}
					/>
				</div>
			))}
		</Section>
	)
}
